// src/app/components/reporting/components/export-actions.tsx
// Install pptxgenjs first: npm install pptxgenjs
import { useState } from 'react';
import { FileText, Mail, Link2, Linkedin, Check, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button, CopyButton, Icon, Modal } from '@/app/components/kit';
import { toast } from 'sonner';
import { createShareLink } from '@/app/services/reporting/insights-api-service';
import type { GeneratedReport } from '../types';

// ── Utilities ─────────────────────────────────────────────────────────────────

function copyText(text: string) {
  if (navigator.clipboard) { navigator.clipboard.writeText(text); return; }
  const el = document.createElement('textarea');
  el.value = text; document.body.appendChild(el); el.select();
  document.execCommand('copy'); document.body.removeChild(el);
}

// ── PowerPoint ────────────────────────────────────────────────────────────────

async function generatePptx(report: GeneratedReport) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mod     = await import('pptxgenjs' as any);
  const pptxgen = mod.default ?? mod;
  const pres    = new pptxgen();
  pres.layout   = 'LAYOUT_WIDE';

  const TEAL = '00B8A9', DARK = '1A1A2E', GRAY = '6B7280', WHITE = 'FFFFFF', LIGHT = 'F0FAFA';

  const s1 = pres.addSlide();
  s1.background = { color: DARK };
  s1.addText(report.title, { x: 0.5, y: 1.2, w: '90%', fontSize: 36, bold: true, color: WHITE });
  s1.addText(`${report.format}  ·  Audience: ${report.audience}  ·  Confidence: ${report.confidence}%`,
    { x: 0.5, y: 2.6, w: '90%', fontSize: 14, color: TEAL });
  s1.addText(new Date(report.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    { x: 0.5, y: 3.1, w: '90%', fontSize: 13, color: GRAY });

  const s2 = pres.addSlide();
  s2.background = { color: WHITE };
  s2.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 0.9, fill: { color: DARK } });
  s2.addText('Executive Summary', { x: 0.5, y: 0.15, w: '90%', fontSize: 20, bold: true, color: WHITE });
  s2.addText(report.executive_summary, { x: 0.5, y: 1.2, w: '90%', h: 3.5, fontSize: 16, color: DARK, wrap: true, valign: 'top' });

  report.sections.forEach((section, idx) => {
    const s = pres.addSlide();
    s.background = { color: idx % 2 === 0 ? WHITE : LIGHT };
    s.addShape(pres.ShapeType.rect, { x: 0, y: 0, w: '100%', h: 0.9, fill: { color: TEAL } });
    s.addText(`${idx + 1}. ${section.title}`, { x: 0.5, y: 0.15, w: '85%', fontSize: 20, bold: true, color: WHITE });
    if (section.key_metric) s.addText(section.key_metric, { x: '88%', y: 0.12, w: 1.2, h: 0.6, fontSize: 18, bold: true, color: TEAL, align: 'right' });
    s.addText(section.content, { x: 0.5, y: 1.1, w: '90%', h: 2.8, fontSize: 15, color: DARK, wrap: true, valign: 'top' });
    if (section.reasoning) {
      s.addShape(pres.ShapeType.rect, { x: 0.5, y: 4.2, w: '90%', h: 0.7, fill: { color: 'EAF8F7' }, line: { color: TEAL, width: 1 } });
      s.addText(`AI: ${section.reasoning}`, { x: 0.7, y: 4.3, w: '88%', fontSize: 11, color: GRAY, italic: true });
    }
  });

  if (report.key_findings.length > 0) {
    const sf = pres.addSlide();
    sf.background = { color: DARK };
    sf.addText('Key Findings', { x: 0.5, y: 0.4, w: '90%', fontSize: 28, bold: true, color: WHITE });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sf.addText(report.key_findings.map((f) => ({ text: f, options: { bullet: true } })) as any,
      { x: 0.5, y: 1.4, w: '90%', h: 4, fontSize: 16, color: TEAL, valign: 'top' });
  }

  await pres.writeFile({ fileName: `${report.title.replace(/[^a-z0-9]/gi, '_')}.pptx` });
}

// ── Email ─────────────────────────────────────────────────────────────────────

function buildEmail(report: GeneratedReport): { subject: string; body: string } {
  const subject = `${report.title} — ${report.format}`;
  const secs    = report.sections.map((s, i) =>
    `${i + 1}. ${s.title}${s.key_metric ? ` (${s.key_metric})` : ''}\n${s.content}`
  ).join('\n\n');
  const findings = report.key_findings.map((f) => `• ${f}`).join('\n');
  const body = `${subject}\n${'─'.repeat(60)}\n\nEXECUTIVE SUMMARY\n${report.executive_summary}\n\n${'─'.repeat(60)}\nSECTIONS\n\n${secs}\n\n${'─'.repeat(60)}\nKEY FINDINGS\n\n${findings}\n\n${'─'.repeat(60)}\nFormat: ${report.format}  |  Audience: ${report.audience}  |  Confidence: ${report.confidence}%\nGenerated: ${new Date(report.created_at).toLocaleDateString()}\n`;
  return { subject, body };
}

// ── LinkedIn ──────────────────────────────────────────────────────────────────

function buildLinkedIn(report: GeneratedReport): string {
  const first   = report.key_findings[0] || report.title;
  const snippet = report.executive_summary.split('. ').slice(0, 2).join('. ').trim();
  const bullets = report.key_findings.slice(0, 4).map((f) => `✅ ${f}`).join('\n');
  const post    = `🚀 ${first}\n\n${snippet}\n\nKey highlights:\n${bullets}\n\n#BrandIntelligence #AIPerception #BrandStrategy #DataDriven`;
  return post.length > 1300 ? post.slice(0, 1297) + '...' : post;
}

// ── Copy Modal ────────────────────────────────────────────────────────────────
//
// Was a hand-rolled overlay: a fixed div, a hardcoded rgba scrim, `z-50` — a
// literal outside the named ladder — and no focus trap, no Escape, no return
// focus. It is the kit's Modal now, which brings all four and the one-scrim
// rule with them.

function CopyModal({ title, text, mailtoHref, onClose }: {
  title: string; text: string; mailtoHref?: string; onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="form"
      actions={
        <>
          <CopyButton value={text} label="Copy to clipboard" confirmedLabel="Copied" />
          {mailtoHref && (
            <a className="k-btn k-btn--sec" href={mailtoHref} target="_blank" rel="noreferrer">
              <Icon icon={Mail} size="sm" />
              Open in email
            </a>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </>
      }
    >
      <textarea
        readOnly
        value={text}
        rows={12}
        aria-label={title}
        className="k-textarea upm-ir-copybox"
      />
    </Modal>
  );
}

type Status = 'idle' | 'loading' | 'done' | 'error';

// ── Export button ─────────────────────────────────────────────────────────────
//
// The DC puts these in the draft's export rail: one 36px button per format,
// icon and label, hover to the brand edge. The two-line card with a
// description under the label is gone — it was a 4-up grid inside a panel that
// no longer exists, and "Copy public URL to clipboard" beside a button reading
// "Shareable link" is the same sentence twice.
//
// The status still shows, because a download that failed has to say so: the
// icon becomes a spinner while it runs and a tick when it lands.

function ExportButton({ icon, label, onClick, status }: {
  icon: LucideIcon; label: string; onClick: () => void; status: Status;
}) {
  const glyph = status === 'loading' ? Loader2 : status === 'done' ? Check : icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={status === 'loading'}
      className="upm-ir-export"
      data-status={status}
      aria-busy={status === 'loading' || undefined}
    >
      <Icon icon={glyph} size="sm" className={status === 'loading' ? 'upm-ir-export__spin' : undefined} />
      {status === 'error' ? `${label} failed, retry` : label}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ExportActions({ report }: { report: GeneratedReport }) {
  type ExportId = 'powerpoint' | 'email' | 'link' | 'linkedin';
  const [statuses, setStatuses] = useState<Record<ExportId, Status>>({
    powerpoint: 'idle', email: 'idle', link: 'idle', linkedin: 'idle',
  });
  const [modal, setModal] = useState<{ title: string; text: string; mailtoHref?: string } | null>(null);

  const set = (id: ExportId, s: Status) => setStatuses((p) => ({ ...p, [id]: s }));

  const handlePptx = async () => {
    set('powerpoint', 'loading');
    try {
      await generatePptx(report);
      set('powerpoint', 'done');
      toast.success('PowerPoint downloaded!');
      setTimeout(() => set('powerpoint', 'idle'), 3000);
    } catch {
      set('powerpoint', 'error');
      toast.error('PowerPoint failed. Run: npm install pptxgenjs');
      setTimeout(() => set('powerpoint', 'idle'), 4000);
    }
  };

  const handleEmail = () => {
    const { subject, body } = buildEmail(report);
    setModal({
      title: 'Email Summary',
      text: body,
      mailtoHref: `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    });
  };

  const handleLink = async () => {
    set('link', 'loading');
    try {
      const { url } = await createShareLink(report.id);
      copyText(url);
      set('link', 'done');
      toast.success('Shareable link copied!', { description: url, duration: 6000 });
      setTimeout(() => set('link', 'idle'), 4000);
    } catch {
      set('link', 'error');
      toast.error('Could not generate share link');
      setTimeout(() => set('link', 'idle'), 3000);
    }
  };

  const handleLinkedIn = () => setModal({ title: 'LinkedIn Post', text: buildLinkedIn(report) });

  return (
    <>
      <ExportButton icon={FileText} label="PowerPoint"     onClick={handlePptx}    status={statuses.powerpoint} />
      <ExportButton icon={Mail}     label="Email"          onClick={handleEmail}   status={statuses.email} />
      <ExportButton icon={Link2}    label="Shareable link" onClick={handleLink}    status={statuses.link} />
      <ExportButton icon={Linkedin} label="LinkedIn"       onClick={handleLinkedIn} status={statuses.linkedin} />
      {modal && (
        <CopyModal
          title={modal.title}
          text={modal.text}
          mailtoHref={modal.mailtoHref}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}
